import { SynchrounousState, type VoidAsyncResult, asVoidAsyncResult } from "@/cache/AsyncResult";
import { networkCaches } from "@/cache/GlobalCache";
import { lightningBinaryCacheId } from "@/cache/LightningBinaryCache";
import { DefaultLightningDuration, getLightningDurationMin, isLightningDuration } from "@/geojson/definitions";
import type { ScenePublicApi } from "@/layers/Compositor";
import type { CheckedProps, PropsChecker } from "@/layers/LayerBase";
import { LayerBase } from "@/layers/LayerBase";
import { normalizeLightningTypeFilter } from "@/layers/geojson/custom/utils";
import { createLightingTooltipFromRows } from "@/layers/geojson/popovers";
import { getAreaFromMap } from "@/layers/utility/mapboxArea";
import { roundToNearestCacheableTime } from "@/time/roundByLayerKind";
import { safeFmt } from "@/utility/safeFmt";
import { Area, CoordinateSystem } from "@mm/api.meteomatics.com";
import Logger from "@mm/logging";
import type { GuiTimeZone, LightningLayer } from "@mm/metx-workbench.meteomatics.com";
import { debounce } from "lodash";
import { DateTime, Duration } from "luxon";
import mapboxgl from "mapbox-gl";
import type * as Mapbox from "mapbox-gl";
import type { LayerKindName } from "../utility/createLayerObject";
import {
  type LightningBinaryInteractionHandle,
  type LightningBinaryInteractionRequest,
  registerLightningBinaryInteraction,
} from "./LightningBinaryInteractionCoordinator";
import type { LightningBinaryPayload } from "./LightningBinaryPayload";
import type { LightningBinaryPickResult } from "./LightningBinaryPicking";
import { decodeLightningBinaryPoint } from "./LightningBinaryPoint";
import {
  type LightningBinaryRenderOptions,
  LightningBinaryRenderer,
  lightningWorldOffsets,
} from "./LightningBinaryRenderer";
import type { LightningBinaryRequest, LightningBinaryType } from "./LightningBinaryRequest";
import { resolveLightningGridResolution } from "./LightningGridResolution";

const logger = Logger.fromFilename(__filename);
const REQUEST_BBOX_BUFFER_RATIO = 0.1;
const WEB_MERCATOR_MAX_LATITUDE = 85.05112878;
const EMPTY_PICK_RESULT: LightningBinaryPickResult = Object.freeze({ hits: Object.freeze([]), truncated: false });

type LightningRequestGrid = Readonly<{
  area: Area<CoordinateSystem.WGS84>;
  width: number;
  height: number;
  zoom: number;
}>;

type PendingPick = Readonly<{
  request: LightningBinaryInteractionRequest;
  snapshot: PresentedLightningSnapshot;
}>;

type LightningPresentationState = Readonly<{
  cacheId: string;
  selectedTimeMs: number;
  durationMs: number;
  typeMask: number;
}>;

type PresentedLightningSnapshot = LightningPresentationState &
  Readonly<{
    payload: LightningBinaryPayload;
  }>;

type DesiredLightningPresentation = LightningPresentationState &
  Readonly<{
    request: LightningBinaryRequest;
  }>;

function bufferedArea(area: Area<CoordinateSystem.WGS84>): Area<CoordinateSystem.WGS84> {
  const longitudePadding = Math.max(0, area.width()) * REQUEST_BBOX_BUFFER_RATIO;
  const latitudePadding = Math.max(0, area.height()) * REQUEST_BBOX_BUFFER_RATIO;
  return new Area(CoordinateSystem.WGS84, {
    west: Math.max(-180, area.west - longitudePadding),
    east: Math.min(180, area.east + longitudePadding),
    south: Math.max(-WEB_MERCATOR_MAX_LATITUDE, area.south - latitudePadding),
    north: Math.min(WEB_MERCATOR_MAX_LATITUDE, area.north + latitudePadding),
  });
}

function containsArea(container: Area<CoordinateSystem.WGS84>, contained: Area<CoordinateSystem.WGS84>): boolean {
  return (
    container.west <= contained.west &&
    container.east >= contained.east &&
    container.south <= contained.south &&
    container.north >= contained.north
  );
}

export function lightningTypeFilterToWireValues(
  filter: string[] | null | undefined,
): LightningBinaryType[] | undefined {
  const normalized = normalizeLightningTypeFilter(filter);
  if (!normalized) {
    return undefined;
  }
  return normalized.map((value) => {
    if (value === "cloud_to_cloud") {
      return 1;
    }
    if (value === "cloud_to_ground") {
      return 2;
    }
    return -999;
  });
}

export function lightningTypeFilterMask(filter: string[] | null | undefined): number {
  const normalized = normalizeLightningTypeFilter(filter);
  if (!normalized) {
    return 0b111;
  }
  return normalized.reduce((mask, value) => {
    if (value === "cloud_to_cloud") {
      return mask | 0b010;
    }
    if (value === "cloud_to_ground") {
      return mask | 0b100;
    }
    return mask | 0b001;
  }, 0);
}

export class LightningBinaryLayerImpl extends LayerBase<LightningLayer> implements Mapbox.CustomLayerInterface {
  readonly id: string;
  readonly type = "custom" as const;
  readonly renderingMode = "2d" as const;

  private readonly renderer = new LightningBinaryRenderer();
  private readonly popup = new mapboxgl.Popup({ maxWidth: "600px", className: "mapbox-popover mapbox-popover-table" });
  private readonly owner: string;
  private interaction?: LightningBinaryInteractionHandle;
  private pendingPick?: PendingPick;
  private completedPick?: PendingPick;
  private latestObservedPromise?: Promise<LightningBinaryPayload>;
  private desiredCacheId?: string;
  private presentedSnapshot?: PresentedLightningSnapshot;
  private stagedSnapshot?: PresentedLightningSnapshot;
  private requestGrid?: LightningRequestGrid;
  private lastSelectedTimeMs?: number;
  private rendererVisible = false;
  private removed = false;

  private readonly debouncedUpdateTrigger = debounce(() => this.updateTrigger(), 300, {
    leading: true,
    maxWait: 1000,
  });

  constructor(uid: number, props: LightningLayer, scene: ScenePublicApi, timezone: GuiTimeZone) {
    super(uid, props, scene, timezone);
    this.id = this.humanReadableId();
    this.owner = `${scene.id}:${this.id}`;
    const map = scene.getMapboxMap();
    map.addLayer(this);
    this.registerInteraction(map);
  }

  get mapboxIndex(): string {
    return this.id;
  }

  humanReadableId(): string {
    return safeFmt`metx.LightningBinaryLayer#${this.uid}#${this.props.model}.${this.props.parameter_unit}`;
  }

  onAdd(map: Mapbox.Map, gl: WebGL2RenderingContext): void {
    this.removed = false;
    this.renderer.onAdd(gl);
    if (this.presentedSnapshot) {
      this.renderer.setPayload(this.presentedSnapshot.payload);
    }
    this.rendererVisible = this.props.show && !!this.presentedSnapshot;
    this.renderer.setVisible(this.rendererVisible);
    this.registerInteraction(map);
    this.scene.repaint();
  }

  onRemove(): void {
    this.removed = true;
    this.rendererVisible = false;
    this.disposeInteraction();
    this.releaseLayer();
    this.renderer.dispose();
  }

  render(_gl: WebGL2RenderingContext, matrix: number[]): void {
    this.commitStagedSnapshot();
    const snapshot = this.presentedSnapshot;
    if (!this.props.show || !snapshot) {
      this.cancelPendingPick();
      return;
    }
    const map = this.scene.getMapboxMap();
    const canvas = map.getCanvas();
    const cssWidth = canvas.clientWidth || canvas.width || 1;
    const devicePixelRatio = canvas.width / cssWidth || globalThis.devicePixelRatio || 1;

    const renderOptions: LightningBinaryRenderOptions = {
      matrix,
      viewportWidth: Math.max(1, canvas.width),
      viewportHeight: Math.max(1, canvas.height),
      devicePixelRatio,
      iconSizeCssPx: Math.max(1, this.props.text_size),
      opacity: this.props.opacity,
      selectedTimeMs: snapshot.selectedTimeMs,
      durationMs: snapshot.durationMs,
      typeMask: snapshot.typeMask,
      worldOffsets: lightningWorldOffsets(map.getCenter().lng),
    };
    this.renderer.render(renderOptions);
    this.performPendingPick(renderOptions);
  }

  createRequest(dateTimeWithOffset: DateTime): LightningBinaryRequest {
    const map = this.scene.getMapboxMap();
    const viewportArea = getAreaFromMap(map);
    const canvas = map.getCanvas();
    const cssWidth = canvas.clientWidth || canvas.width / (globalThis.devicePixelRatio || 1) || 1;
    const cssHeight = canvas.clientHeight || canvas.height / (globalThis.devicePixelRatio || 1) || 1;
    const candidateArea = bufferedArea(viewportArea);
    const widthScale = viewportArea.width() > 0 ? candidateArea.width() / viewportArea.width() : 1;
    const heightScale = viewportArea.height() > 0 ? candidateArea.height() / viewportArea.height() : 1;
    const candidateResolution = resolveLightningGridResolution({
      viewportWidthCss: cssWidth,
      viewportHeightCss: cssHeight,
      coverageScaleX: widthScale,
      coverageScaleY: heightScale,
    });
    const zoom = map.getZoom();
    const previousRequestGrid = this.requestGrid;
    // Iceberg's aggregation cells are relative to the request bbox. Retaining
    // the buffered grid prevents its selected strikes from changing on every pan.
    const requestGrid: LightningRequestGrid =
      previousRequestGrid &&
      previousRequestGrid.zoom === zoom &&
      previousRequestGrid.width === candidateResolution.width &&
      previousRequestGrid.height === candidateResolution.height &&
      containsArea(previousRequestGrid.area, viewportArea)
        ? previousRequestGrid
        : {
            area: candidateArea,
            width: candidateResolution.width,
            height: candidateResolution.height,
            zoom,
          };
    this.requestGrid = requestGrid;

    return {
      area: requestGrid.area,
      datetime: dateTimeWithOffset,
      timeDuration: Duration.fromObject({ minutes: getLightningDurationMin(this.props.parameter_unit) }),
      width: requestGrid.width,
      height: requestGrid.height,
      typeFilter: lightningTypeFilterToWireValues(this.readTypeFilter()),
      wireVersion: 3,
      // Iceberg's deployed lightning endpoint currently returns
      // newest_per_cell for this wire format, including when another mode is
      // requested. Keep the request aligned with the response envelope.
      aggregation: "newest_per_cell",
    };
  }

  updateTrigger(): void {
    if (this.removed || !this.props.show) {
      return;
    }
    const desired = this.createDesiredPresentation();
    this.desiredCacheId = desired.cacheId;
    if (this.stagedSnapshot?.cacheId !== desired.cacheId) {
      this.stagedSnapshot = undefined;
    }
    const result = networkCaches.lightning_binary_cache.retrieve(desired.request, { owner: this.owner });

    if (result.synchronous === SynchrounousState.StillPending) {
      networkCaches.lightning_binary_cache.cancelSuperseded(this.owner, result.asynchronous);
      if (this.latestObservedPromise !== result.asynchronous) {
        this.latestObservedPromise = result.asynchronous;
        result.asynchronous
          .then(() => {
            if (!this.removed && this.desiredCacheId === desired.cacheId) {
              this.updateTrigger();
            }
          })
          .catch((error) => {
            if (!this.removed && !error?.isCancelled && error?.kind !== "Abort") {
              logger.warn("Unable to load binary lightning data", error);
              if (this.desiredCacheId === desired.cacheId) {
                this.updateTrigger();
              }
            }
          });
      }
      return;
    }

    if (
      result.synchronous === SynchrounousState.NotRequested ||
      result.synchronous === SynchrounousState.PermanentlyFailed
    ) {
      this.clearPresentation();
      return;
    }

    const snapshot: PresentedLightningSnapshot = {
      cacheId: desired.cacheId,
      payload: result.synchronous,
      selectedTimeMs: desired.selectedTimeMs,
      durationMs: desired.durationMs,
      typeMask: desired.typeMask,
    };
    if (this.snapshotsMatch(this.presentedSnapshot, snapshot)) {
      this.stagedSnapshot = undefined;
      return;
    }
    if (!this.snapshotsMatch(this.stagedSnapshot, snapshot)) {
      this.stagedSnapshot = snapshot;
      this.scene.repaint();
    }
  }

  private createDesiredPresentation(): DesiredLightningPresentation {
    const selectedTime = this.getRequestDateTime(this.scene.getDisplayTimeWithOffset());
    const request = this.createRequest(selectedTime);
    return {
      request,
      cacheId: lightningBinaryCacheId(request),
      selectedTimeMs: selectedTime.toMillis(),
      durationMs: this.selectedDurationMs(),
      typeMask: lightningTypeFilterMask(this.readTypeFilter()),
    };
  }

  private commitStagedSnapshot(): void {
    const staged = this.stagedSnapshot;
    if (!staged || !this.props.show) {
      return;
    }

    const desired = this.createDesiredPresentation();
    this.desiredCacheId = desired.cacheId;
    if (staged.cacheId !== desired.cacheId) {
      this.stagedSnapshot = undefined;
      return;
    }

    const snapshot: PresentedLightningSnapshot = {
      ...staged,
      selectedTimeMs: desired.selectedTimeMs,
      durationMs: desired.durationMs,
      typeMask: desired.typeMask,
    };
    this.cancelPendingPick();
    this.renderer.setPayload(snapshot.payload);
    this.presentedSnapshot = snapshot;
    this.stagedSnapshot = undefined;
    this.setRendererVisible(true);
  }

  private snapshotsMatch(left: PresentedLightningSnapshot | undefined, right: PresentedLightningSnapshot): boolean {
    return (
      left?.cacheId === right.cacheId &&
      left.payload.buffer === right.payload.buffer &&
      left.selectedTimeMs === right.selectedTimeMs &&
      left.durationMs === right.durationMs &&
      left.typeMask === right.typeMask
    );
  }

  beforeRender(): void {
    if (!this.props.show || this.removed) {
      return;
    }
    const selectedTime = this.getRequestDateTime(this.scene.getDisplayTimeWithOffset()).toMillis();
    if (selectedTime !== this.lastSelectedTimeMs) {
      this.cancelPendingPick();
      this.lastSelectedTimeMs = selectedTime;
      this.debouncedUpdateTrigger.cancel();
      this.updateTrigger();
      return;
    }
    if (this.scene.isAnimation()) {
      this.updateTrigger();
      return;
    }
    this.debouncedUpdateTrigger();
  }

  fetchData(timeFrame: DateTime): VoidAsyncResult {
    const selectedTime = this.getRequestDateTime(this.scene.getDateTimeWithOffset(timeFrame));
    return asVoidAsyncResult(networkCaches.lightning_binary_cache.retrieve(this.createRequest(selectedTime)));
  }

  isRebuffering(timeFrame: DateTime): boolean {
    const selectedTime = this.getRequestDateTime(this.scene.getDateTimeWithOffset(timeFrame));
    return (
      networkCaches.lightning_binary_cache.peek(this.createRequest(selectedTime)).synchronous ===
      SynchrounousState.StillPending
    );
  }

  checker(): PropsChecker<LightningLayer, LayerBase<LightningLayer>> {
    type CheckerProp = CheckedProps<LightningLayer>;
    const check =
      (property: CheckerProp) =>
      (previous: LightningLayer, current: LightningLayer): boolean => {
        if (previous[property] === current[property]) {
          return false;
        }
        this.setLayerProps({ [property]: current[property] });
        this.cancelPendingPick();
        if (property === "show") {
          this.setRendererVisible(current.show && !!this.presentedSnapshot);
        }
        this.scene.repaint();
        return true;
      };
    return {
      legend_visible: check("legend_visible"),
      model: check("model"),
      opacity: check("opacity"),
      calibrated: check("calibrated"),
      parameter_unit: check("parameter_unit"),
      show: check("show"),
      text_size: check("text_size"),
      text_color: check("text_color"),
      ens_select: check("ens_select"),
      custom_options: check("custom_options"),
    };
  }

  getActiveWeatherParametersAsString(): { model: string; parameter: string }[] {
    return [];
  }

  releaseLayer(): void {
    this.debouncedUpdateTrigger.cancel();
    networkCaches.lightning_binary_cache.releaseOwner(this.owner);
  }

  removeLayer(): void {
    if (this.removed) {
      return;
    }
    this.removed = true;
    this.releaseLayer();
    const map = this.scene.getMapboxMap();
    if (map.getLayer(this.id)) {
      map.removeLayer(this.id);
    } else {
      this.disposeInteraction();
      this.renderer.dispose();
    }
    this.desiredCacheId = undefined;
    this.stagedSnapshot = undefined;
    this.presentedSnapshot = undefined;
    this.latestObservedPromise = undefined;
  }

  private canPick(): boolean {
    return (
      !this.removed && this.props.show && this.props.opacity > 0 && this.rendererVisible && !!this.presentedSnapshot
    );
  }

  private queuePick(request: LightningBinaryInteractionRequest): void {
    const snapshot = this.presentedSnapshot;
    if (!this.canPick() || !snapshot) {
      this.interaction?.report(request.token, EMPTY_PICK_RESULT);
      return;
    }
    this.completedPick = undefined;
    this.pendingPick = {
      request,
      snapshot,
    };
    this.scene.repaint();
  }

  private performPendingPick(renderOptions: LightningBinaryRenderOptions): void {
    const pending = this.pendingPick;
    if (!pending) {
      return;
    }
    this.pendingPick = undefined;
    if (
      !this.canPick() ||
      this.presentedSnapshot !== pending.snapshot ||
      renderOptions.selectedTimeMs !== pending.snapshot.selectedTimeMs ||
      renderOptions.durationMs !== pending.snapshot.durationMs ||
      renderOptions.typeMask !== pending.snapshot.typeMask
    ) {
      this.completedPick = undefined;
      this.interaction?.report(pending.request.token, EMPTY_PICK_RESULT);
      return;
    }

    try {
      const result = this.renderer.pick({
        ...renderOptions,
        clickXCss: pending.request.point.x,
        clickYCss: pending.request.point.y,
      });
      this.completedPick = pending;
      this.interaction?.report(pending.request.token, result);
    } catch (error) {
      this.completedPick = undefined;
      logger.warn("Unable to pick binary lightning data", error);
      this.interaction?.report(pending.request.token, EMPTY_PICK_RESULT);
    }
  }

  private presentPick(request: LightningBinaryInteractionRequest, result: LightningBinaryPickResult): void {
    const completed = this.completedPick;
    this.completedPick = undefined;
    if (
      !completed ||
      completed.request.token !== request.token ||
      !this.canPick() ||
      this.presentedSnapshot !== completed.snapshot
    ) {
      return;
    }

    const decoded = result.hits
      .map((hit) => {
        const point = decodeLightningBinaryPoint(completed.snapshot.payload, hit.pointIndex);
        return point ? { point, distanceSquaredCssPx: hit.distanceSquaredCssPx } : undefined;
      })
      .filter((value): value is NonNullable<typeof value> => !!value)
      .sort(
        (left, right) =>
          left.distanceSquaredCssPx - right.distanceSquaredCssPx ||
          right.point.strikeTimeMs - left.point.strikeTimeMs ||
          left.point.pointIndex - right.point.pointIndex,
      );
    if (decoded.length === 0) {
      return;
    }
    if (result.truncated) {
      logger.debug("Binary lightning popup results were truncated", { rows: decoded.length });
    }

    const rows = decoded.map(({ point }) => ({
      coordinates: [point.longitude, point.latitude] as const,
      strikeTime: DateTime.fromMillis(point.strikeTimeMs, { zone: this.timezone }),
      polarity: point.polarity,
      currentKa: point.currentKa,
      lightningType:
        point.lightningType === 1
          ? ("cloud_to_cloud" as const)
          : point.lightningType === 2
            ? ("cloud_to_ground" as const)
            : null,
    }));
    const nearest = decoded[0].point;
    let anchorLongitude = nearest.longitude;
    while (Math.abs(request.lngLat.lng - anchorLongitude) > 180) {
      anchorLongitude += request.lngLat.lng > anchorLongitude ? 360 : -360;
    }
    this.popup
      .setLngLat(new mapboxgl.LngLat(anchorLongitude, nearest.latitude))
      .setHTML(createLightingTooltipFromRows(rows))
      .addTo(this.scene.getMapboxMap());
  }

  private cancelPendingPick(): void {
    const pending = this.pendingPick;
    this.pendingPick = undefined;
    this.completedPick = undefined;
    if (pending) {
      this.interaction?.report(pending.request.token, EMPTY_PICK_RESULT);
    }
  }

  private selectedDurationMs(): number {
    const selectedDuration = isLightningDuration(this.props.parameter_unit)
      ? this.props.parameter_unit
      : DefaultLightningDuration;
    return Duration.fromObject({ minutes: getLightningDurationMin(selectedDuration) }).as("milliseconds");
  }

  private setRendererVisible(visible: boolean): void {
    this.rendererVisible = visible;
    this.renderer.setVisible(visible);
    if (!visible) {
      this.cancelPendingPick();
    }
  }

  private clearPresentation(): void {
    const hadPresentation = this.rendererVisible || !!this.presentedSnapshot || !!this.stagedSnapshot;
    this.stagedSnapshot = undefined;
    this.presentedSnapshot = undefined;
    this.setRendererVisible(false);
    if (hadPresentation) {
      this.scene.repaint();
    }
  }

  private disposeInteraction(): void {
    this.cancelPendingPick();
    this.interaction?.unregister();
    this.interaction = undefined;
    this.popup.remove();
  }

  private registerInteraction(map: Mapbox.Map): void {
    if (this.interaction) {
      return;
    }
    this.interaction = registerLightningBinaryInteraction(map, {
      id: this.id,
      canPick: () => this.canPick(),
      queuePick: (request) => this.queuePick(request),
      cancelPick: () => this.cancelPendingPick(),
      presentPick: (request, result) => this.presentPick(request, result),
    });
  }

  private getRequestDateTime(dateTime: DateTime): DateTime {
    const base = roundToNearestCacheableTime(dateTime, this.props.kind as LayerKindName);
    return this.scene.isAnimation() ? base.startOf("minute") : base;
  }

  private readTypeFilter(): string[] | null {
    const customOptions = this.props.custom_options as { lightning_type_filter?: string[] | null } | undefined;
    return Array.isArray(customOptions?.lightning_type_filter) ? customOptions.lightning_type_filter : null;
  }
}
